import { Fetcher } from '../core';
import { SuccessfulResponse } from '../core/fetcher/APIResponse';

import { createFetcherWithMiddlewares, Middleware } from './middleware';

export const createFetcherArgs = (args: Fetcher.Args) => args;

export const createFetcherResp = (resp: { body: any; headers?: Record<string, any> }): SuccessfulResponse<any> => ({
    ok: true,
    body: resp.body,
    headers: resp.headers,
    rawResponse: {
        url: '',
        status: 0,
        type: 'default',
        redirected: false,
        statusText: 'Test',
        headers: new Headers(resp.headers),
    },
});

export const createMockedFetcher = () =>
    jest.fn().mockResolvedValue(
        createFetcherResp({
            body: {},
        }),
    );

const createHeaderMiddleware = (headerName: string, value: string): Middleware<any> => ({
    process: (args, next) =>
        next({
            ...args,
            headers: {
                [headerName]: value,
            },
        }),
});

describe('Fetcher with middlewares', () => {
    it('should create with empty middlewares', async () => {
        const fetcher = createMockedFetcher();
        const fetcherWithMiddlewares = createFetcherWithMiddlewares(fetcher, []);

        const result = await fetcherWithMiddlewares({
            url: 'test',
            method: 'GET',
        });

        expect(fetcher.mock.calls.length).toEqual(1);
        expect(fetcher.mock.lastCall[0]).toEqual(
            createFetcherArgs({
                url: 'test',
                method: 'GET',
            }),
        );

        expect(result).toEqual(
            createFetcherResp({
                body: {},
            }),
        );
    });

    it('should apply a middleware to modify request headers', async () => {
        const fetcher = createMockedFetcher();
        const fetcherWithMiddlewares = createFetcherWithMiddlewares(fetcher, [createHeaderMiddleware('foo', 'bar')]);

        await fetcherWithMiddlewares({
            url: 'test',
            method: 'GET',
        });

        expect(fetcher.mock.calls.length).toEqual(1);
        expect(fetcher.mock.lastCall[0]).toEqual(
            createFetcherArgs({
                url: 'test',
                method: 'GET',
                headers: {
                    foo: 'bar',
                },
            }),
        );
    });

    it('should apply a middleware to modify response headers', async () => {
        const fetcher = createMockedFetcher();
        const fetcherWithMiddlewares = createFetcherWithMiddlewares(fetcher, [
            {
                process: async (args, next) => {
                    const response = await next(args);

                    if (!response.ok) return response;

                    return createFetcherResp({
                        body: response.body,
                        headers: {
                            foo: 'bar',
                        },
                    });
                },
            },
        ]);

        const result = await fetcherWithMiddlewares({
            url: 'test',
            method: 'GET',
        });

        expect(fetcher.mock.calls.length).toEqual(1);
        expect(result).toEqual(
            createFetcherResp({
                body: {},
                headers: {
                    foo: 'bar',
                },
            }),
        );
    });

    it('should apply middlewares in the correct order', async () => {
        const fetcher = createMockedFetcher();
        const fetcherWithMiddlewares = createFetcherWithMiddlewares(fetcher, [
            createHeaderMiddleware('foo', 'bar'),
            createHeaderMiddleware('foo', 'baz'),
        ]);

        await fetcherWithMiddlewares({
            url: 'test',
            method: 'GET',
        });

        expect(fetcher.mock.calls.length).toEqual(1);
        expect(fetcher.mock.lastCall[0]).toEqual(
            createFetcherArgs({
                url: 'test',
                method: 'GET',
                headers: {
                    foo: 'baz',
                },
            }),
        );
    });
});
